import { useState } from "react";
import { Plus, ScrollText, History } from "lucide-react";
import { PageShell } from "./page-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState, ErrorState } from "@/components/common/empty-state";
import { FieldRow } from "./courses";
import { useInstructions, useInstructionVersions, useSaveInstruction } from "@/lib/queries";
import { useToast } from "@/components/ui/toast";
import { formatRelative } from "@/lib/utils";
import type { InstructionSet } from "@/lib/types";

export function InstructionsPage() {
  const { data, isLoading, isError, refetch } = useInstructions();
  const [editing, setEditing] = useState<InstructionSet | "new" | null>(null);
  const [historyFor, setHistoryFor] = useState<InstructionSet | null>(null);

  return (
    <PageShell
      title="Instructions"
      description="System prompts and playbook overrides loaded into the agent."
      actions={
        <Button variant="primary" onClick={() => setEditing("new")}>
          <Plus className="h-4 w-4" />
          New instruction
        </Button>
      }
    >
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : isError ? (
        <ErrorState title="Couldn't load instructions" onRetry={() => void refetch()} />
      ) : !Array.isArray(data) || data.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title="No instructions yet"
          description="Add a global instruction or a playbook override to shape the bot's behavior."
        />
      ) : (
        <ul className="grid gap-3 lg:grid-cols-2">
          {data.map((instruction) => (
            <li key={instruction.id} className="rounded-lg border border-line bg-surface p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-medium text-ink">{instruction.name}</h3>
                  <div className="mt-1 flex items-center gap-1.5">
                    <Badge tone="outline">{instruction.type}</Badge>
                    {instruction.is_active ? (
                      <Badge tone="success">active</Badge>
                    ) : (
                      <Badge tone="neutral">inactive</Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setHistoryFor(instruction)}>
                    <History className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setEditing(instruction)}>
                    Edit
                  </Button>
                </div>
              </div>
              <p className="mt-3 line-clamp-3 text-sm text-ink-muted whitespace-pre-wrap">
                {instruction.content}
              </p>
              <p className="mt-3 text-xs text-ink-faint">
                Updated {formatRelative(instruction.updated_at)}
              </p>
            </li>
          ))}
        </ul>
      )}

      {editing !== null && (
        <InstructionDialog
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      {historyFor && <HistoryDialog instruction={historyFor} onClose={() => setHistoryFor(null)} />}
    </PageShell>
  );
}

function InstructionDialog({ initial, onClose }: { initial: InstructionSet | null; onClose: () => void }) {
  const save = useSaveInstruction();
  const toast = useToast();
  const [form, setForm] = useState<Partial<InstructionSet>>(
    initial || { name: "", type: "global", content: "", is_active: true }
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await save.mutateAsync({ id: initial?.id, body: form });
      toast.success(initial ? "Instruction updated" : "Instruction created");
      onClose();
    } catch (err) {
      toast.error("Save failed", err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit instruction" : "New instruction"}</DialogTitle>
          <DialogDescription>
            Type can be <code>global</code>, <code>playbook</code>, or <code>objection</code>.
          </DialogDescription>
        </DialogHeader>

        <form id="instruction-form" onSubmit={onSubmit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <FieldRow label="Name" id="name">
              <Input
                id="name"
                value={form.name ?? ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </FieldRow>
            <FieldRow label="Type" id="type">
              <Input
                id="type"
                value={form.type ?? "global"}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              />
            </FieldRow>
          </div>
          <FieldRow label="Content" id="content">
            <Textarea
              id="content"
              rows={14}
              value={form.content ?? ""}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              className="font-mono text-sm"
              required
            />
          </FieldRow>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={form.is_active ?? true}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="h-4 w-4 rounded border-line-strong text-accent focus-visible:ring-accent"
            />
            Active
          </label>
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="instruction-form" variant="primary" disabled={save.isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ instruction, onClose }: { instruction: InstructionSet; onClose: () => void }) {
  const { data, isLoading } = useInstructionVersions(instruction.id);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{instruction.name}</DialogTitle>
          <DialogDescription>Version history.</DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto scrollbar-thin">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : !Array.isArray(data) || data.length === 0 ? (
            <EmptyState icon={History} title="No versions yet" />
          ) : (
            <ol className="space-y-3">
              {data.map((v) => (
                <li key={v.id} className="rounded-md border border-line bg-surface p-3">
                  <div className="mb-1.5 flex items-center justify-between text-xs">
                    <span className="font-medium text-ink">v{v.version}</span>
                    <span className="text-ink-faint">{formatRelative(v.created_at)}</span>
                  </div>
                  <pre className="whitespace-pre-wrap font-mono text-xs text-ink-muted">{v.content}</pre>
                </li>
              ))}
            </ol>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
